import React, { useState, useEffect, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import _ from 'lodash'
import { Checkbox } from "primereact/checkbox"
import { Dialog } from "primereact/dialog"
import { Sidebar } from 'primereact/sidebar'

import "./index.css"

import { Button, CalendarField, Table, DropDownField } from "../../helpers"

import Loading from '../../common/Loading'
import PMUserName from '../../common/PMUserName'
import PMCompanyName from '../../common/PMCompanyName'
import ButtonGroup from '../../common/ButtonGroup'
import Menu from "../../helpers/Menu"
import Description from '../../common/Description'
import Search from './components/Search'
import CopyToClipboard from '../../common/CopyToClipboard'

import { raiseToast } from '../../services/redux/reducers/toast/toast'
import { updateBuyerInvoiceData, updateSearchParams, updatePaginationParams, setIsSearchVisible } from "../../services/redux/reducers/adminAccountBuyer/adminAccountsBuyer"
import { updatePaymentConfirmationDate, updateGRN } from '../../services/api/adminAccountBuyer'
import { getBuyerInvoiceItems } from '../../services/api/buyerInvoiceItem'
import { getMaterialItemsDataByBuyerReqItemIds } from '../../services/api/buyerReqItem'
import { getCompanyBillAddrs } from '../../services/api/companyBillAddr'
import { setInitialLoad } from '../../services/redux/reducers/Requirement/requirement'
import { fetchMaterialUoms } from '../../services/redux/reducers/commonThunk'

import { getDateString, getCompanyShortName, numberConversion, getFullName, isMobile, generateReplacedProducts, round } from '../../utils'
import { getPMCompanyConfig } from '../../setup/pmCompanyConfig'
import { useSearchPin } from '../../hooks/useSearchPin'
import { getDocItemReplacementLogAndMapByDocIds } from '../../services/api/docItemReplacementLog'


const AdminAccountsBuyer = () => {
    const dispatch = useDispatch()
    const { isSearchPinned, toggleSearchPin, setSearchPin } = useSearchPin()

    const [ GRNModalVisible, setGRNModalVisible ] = useState(false)
    const [ paymentDateModalVisible, setPaymentModalVisible ] = useState(false)
    const [ GRNUpdateObj, setGRNUpdateObj ] = useState({})
    const [ paymentConfirmationDate, setPaymentConfirmationDate ] = useState()
    const [ selectedBuyerInvoiceIds, setSelectedBuyerInvoiceIds ] = useState([])
    const [dataType, setDataType] = useState("buyerInvoice")
    const [isLoading, setIsLoading] = useState(false)

    const [adjustmentNoteItems, setAdjustmentNoteItems] = useState([])
    const [buyerInvoiceItems, setBuyerInvoiceItems] = useState([])
    const [pmBillAddresses, setPMBillAddresses] = useState([])
    const [materialDetails, setMaterialDetails] = useState([])
    const [docReplacementLogItems, setDocReplacementLogItems] = useState([])
    const [isHoveringSearchZone, setIsHoveringSearchZone] = useState(false)
    const [hasEverHoveredRightEdge, setHasEverHoveredRightEdge] = useState(false)

    const buyerInvoices = useSelector(state => state.adminAccountsBuyerSlice.buyerInvoiceData)
    const isPageLoading = useSelector(state => state.adminAccountsBuyerSlice.loading)
    const paginationParams = useSelector(state => state.adminAccountsBuyerSlice.paginationParams)
    const isInitialLoad = useSelector(state => state.adminAccountsBuyerSlice.isInitialLoad)
    const searchParams = useSelector(state => state.adminAccountsBuyerSlice.searchParams)
    const isSearchVisible = useSelector(state => state.adminAccountsBuyerSlice.isSearchVisible)
    const isSearchActive = useSelector(state => state.adminAccountsBuyerSlice.isSearchActive)
    const totalBasicAmount = useSelector(state => state.adminAccountsBuyerSlice.totalBasicAmount)
    
    const states = useSelector(state => state.common.states)

    const userDetails = useSelector((state) => state.userDetails)

    const pmCompanyConfig = getPMCompanyConfig(userDetails.PMCompanyDetails.companyId)
    const numberLocale = pmCompanyConfig.numberLocale

    const actionMenu = useRef(null)

    const isMobileDevice = isMobile()

    useEffect(() => {
        getIntialLoadData()
        dispatch(fetchMaterialUoms())
    }, [])

    useEffect(() => {
        if (!isSearchPinned && !isHoveringSearchZone) {
            dispatch(setIsSearchVisible(false))
          }

        const handleMouseMove = (e) => {
            if (isSearchPinned) return

            const panelWidth = window.innerWidth * 0.18
            const buffer = 8
            const screenRightEdge = window.innerWidth
            const activeZoneLeft = screenRightEdge - (panelWidth + buffer)

            if (e.clientX < activeZoneLeft) {
                setIsHoveringSearchZone(false)
                setHasEverHoveredRightEdge(false)
            } else {
                setIsHoveringSearchZone(true)
            }
        }

        if (!isSearchPinned && (isHoveringSearchZone || hasEverHoveredRightEdge)) {
            window.addEventListener('mousemove', handleMouseMove)
        }

        return () => {
            window.removeEventListener('mousemove', handleMouseMove)
        }
    }, [isSearchPinned, isHoveringSearchZone, hasEverHoveredRightEdge])

    const getIntialLoadData = () => {
        if(isInitialLoad){
            let toDate = new Date()
            toDate.setHours(23, 59, 0, 0)
            toDate = toDate.toISOString()

            let fromDate = new Date()
            fromDate.setDate(fromDate.getDate() - 30)
            fromDate.setHours(0, 0, 0, 0)
            fromDate = fromDate.toISOString()

            const updatedSearchParams = {
                ...searchParams,
                fromDate: fromDate,
                toDate: toDate
            }

            dispatch(updateSearchParams(updatedSearchParams))
            dispatch(setInitialLoad(false))
        }
    }
    
    useEffect(() => {
        handleTableColumn(searchParams)
    }, [buyerInvoices, selectedBuyerInvoiceIds, materialDetails, pmBillAddresses, buyerInvoiceItems, docReplacementLogItems])

    useEffect(() => {
        if ((dataType === "buyerInvoice" || dataType === "accountInvoice") && buyerInvoices?.length > 0){
            getBuyerInvoiceItemsData(buyerInvoices)
        }
        if ((dataType === "salesInvoice" || dataType === "productsInvoice") && buyerInvoices?.length > 0){
            getMaterialDetails(buyerInvoices)
            getPMBillAddress(buyerInvoices)
        }
    }, [buyerInvoices])

    useEffect(() => {
        if (dataType === "creditNotesData" && buyerInvoices?.length > 0){
            getAdjustmentNoteItemsDetails()
        }
    }, [buyerInvoices])

    const getBuyerInvoiceItemsData = async (invoiceItems) => {
        let buyerInvoiceIds = invoiceItems?.map(eachBuyerInvoice => eachBuyerInvoice?.buyerInvoiceId)
        const buyerInvoiceItemsRes = await getBuyerInvoiceItems({buyerInvoiceIds: buyerInvoiceIds, paginator: false})

        if (buyerInvoiceItemsRes?.failure){
            dispatch(raiseToast({severity:'error', summary: "Buyer Invoice Items", detail: buyerInvoiceItemsRes?.data?.error, life: 3000}))
        }
        setBuyerInvoiceItems(buyerInvoiceItemsRes?.data)
        await getMaterialDetails(buyerInvoiceItemsRes?.data)
        await getDocItemReplacementLogs(buyerInvoiceIds)
    }

    const getMaterialDetails = async(invoiceItems) => {
        const buyerReqItemsIds = invoiceItems.map(eachBuyerInvoiceItem => eachBuyerInvoiceItem?.buyerReqItemId).filter(eachBuyerInvoiceItemId => eachBuyerInvoiceItemId)
        
        const materialDetailsRes = await getMaterialItemsDataByBuyerReqItemIds({buyerReqItemsIds: buyerReqItemsIds})

        if (materialDetailsRes?.failure){
            dispatch(raiseToast({severity:'error', summary: "Material Details", detail: materialDetailsRes?.data?.error, life: 3000}))
        }

        setMaterialDetails(materialDetailsRes?.data)
    }

    const getDocItemReplacementLogs = async (docIds) => {
        const docReplacementLogItemsRes = await getDocItemReplacementLogAndMapByDocIds({docIds: docIds})
        if (docReplacementLogItemsRes?.failure){
            dispatch(raiseToast({severity:'error', summary: "Doc Item Replacement Log", detail: docReplacementLogItemsRes?.data?.error, life: 3000}))
        }

        setDocReplacementLogItems(docReplacementLogItemsRes?.data)

    }

    const getAdjustmentNoteItemsDetails = async () => {
        setAdjustmentNoteItems(buyerInvoices)
        const relatedBuyerInvoiceItemIds = buyerInvoices?.map(eachBuyerInvoice => eachBuyerInvoice?.buyerInvoiceItemId)
        const buyerInvoiceItemsRes = await getBuyerInvoiceItems({buyerInvoiceItemIds: relatedBuyerInvoiceItemIds, paginator: false, joinRequired: true})

        if (buyerInvoiceItemsRes?.failure){
            dispatch(raiseToast({severity:'error', summary: "Buyer Invoice Items", detail: buyerInvoiceItemsRes?.data?.error, life: 3000}))
        }
        setBuyerInvoiceItems(buyerInvoiceItemsRes?.data)
        await getMaterialDetails(buyerInvoiceItemsRes?.data)
        await getPMBillAddress(buyerInvoiceItemsRes?.data)

    }

    const getPMBillAddress = async (buyerInvoiceItems) => {
        const pmBillAddrsIds = _.uniq(_.map(buyerInvoiceItems, 'buyerInvoice.pmBillAddrId'));
        const pmBillAddressRes = await getCompanyBillAddrs({billAddrIds: pmBillAddrsIds, paginator: false})

        if (pmBillAddressRes?.failure){
            dispatch(raiseToast({severity:'error', summary: "Material Details", detail: pmBillAddressRes?.data?.error, life: 3000}))
        }

        setPMBillAddresses(pmBillAddressRes?.data?.data)
    }

    const actionMenuItems = [
        {
            label: 'Update Payment Date',
            icon: '',
            command: () => {
                if (selectedBuyerInvoiceIds.length === 0) {
                    return dispatch(raiseToast({ severity: 'warn', detail: 'Please select invoices', life: 3000 }))
                }
                setPaymentModalVisible(true)
            },
            permissions: [51]
        },
        {
            label: 'Update GRN',
            icon: '',
            command: () => {
                if (selectedBuyerInvoiceIds.length === 0) {
                    return dispatch(raiseToast({ severity: 'warn', detail: 'Please select invoices', life: 3000 }))
                }
                setGRNModalVisible(true)
            },
            permissions: [52]
        },
    ]

    const getKamName = (row) => {
        let kamName = row["kamUser.firstName"] 

        if(row["kamUser.lastName"]){
            kamName = kamName + " " + row["kamUser.lastName"]
        }
        return <PMUserName userName={kamName} userId={row.createdBy}/>
    }

    const getUserName = (row) => {
        let userName = row["user.firstName"] 

        if(row["user.lastName"]){
            userName = userName + " " + row["user.lastName"]
        }
        return <div> { userName } </div>
    }

    const getBuyerPODateBodyItemLevel = (row) => {
        if (row["buyerInvoice.buyerPODate"]){
            let dateString = getDateString(row["buyerInvoice.buyerPODate"])
            return (
                <div className="text-center">
                    <div className="">{dateString}</div>
                </div>
            )
        }else {
            return <div> - </div>
        }
    }

    const getBasicPrice = (row) => {
        return numberConversion(row.totalAmountBeforeTax - row.logisticsBeforeTax, numberLocale)
    }

    const getBasicPriceItemLevel = (row) => {
        return numberConversion(row.itemAmountBeforeTax, numberLocale)
    }

    const getCreditNotesBasicPriceBody = (row) => {
        const itemAmountBeforeTax = parseFloat(row?.adjustedQty) * parseFloat(row?.adjustedPricePerUnit)
        return numberConversion(parseFloat(itemAmountBeforeTax), numberLocale)
    }

    const getCreditNotesTotalAmountBody = (row) => {
        const itemAmountBeforeTax = parseFloat(row?.adjustedQty) * parseFloat(row?.adjustedPricePerUnit)
        const relatedBuyerInvoiceItem = buyerInvoiceItems?.find(eachBuyerInvoiceItem => eachBuyerInvoiceItem?.buyerInvoiceItemId === row?.buyerInvoiceItemId)
        let itemAmountWithTax = itemAmountBeforeTax + itemAmountBeforeTax * relatedBuyerInvoiceItem?.GSTRate * 0.01
        return numberConversion(itemAmountWithTax, numberLocale)
    }

    const getCreditNoteGSTINBody = (row) => {
        const relatedBuyerInvoiceItem = buyerInvoiceItems?.find(eachBuyerInvoiceItem => eachBuyerInvoiceItem?.buyerInvoiceItemId === row?.buyerInvoiceItemId)
        return (
            <>
                <div>{relatedBuyerInvoiceItem?.["buyerInvoice.billingAddress.gstinCode"]}</div>
            </>
        )
    }

    const getCreditNoteProcmartStateBody = (row) => {
        const relatedBuyerInvoiceItem = buyerInvoiceItems?.find(eachBuyerInvoiceItem => eachBuyerInvoiceItem?.buyerInvoiceItemId === row?.buyerInvoiceItemId)
        let relatedPMBillAddress = pmBillAddresses?.find(eachPMBillAddress => eachPMBillAddress?.billAddrId === relatedBuyerInvoiceItem?.["buyerInvoice.pmBillAddrId"])
        let relatedPMState = states?.find(eachState => eachState?.stateId === parseInt(relatedPMBillAddress?.gstinCode?.slice(0, 2)))
        return (
            <>
                <div>{relatedPMState?.stateName}</div>
            </>
        )
    }

    const selectAllBuyerInvoices = (event) => {
        let updatedSelectedInvoice = []
        
        if (selectedBuyerInvoiceIds.length === buyerInvoices.length) {
            updatedSelectedInvoice = []
        }
        else {
            updatedSelectedInvoice = buyerInvoices.map(eachInvoice => eachInvoice.buyerInvoiceId)
        }
        setSelectedBuyerInvoiceIds(updatedSelectedInvoice)
    }

    const handleSelectedBuyerInvoice = (buyerInvoiceId) => {
        let relatedBuyerInvoicesIds = []
        if (selectedBuyerInvoiceIds.includes(buyerInvoiceId)) {
            relatedBuyerInvoicesIds = selectedBuyerInvoiceIds.filter(id => id === buyerInvoiceId ? false : true)
        }
        else {
            relatedBuyerInvoicesIds = [...selectedBuyerInvoiceIds, buyerInvoiceId]
        }
        setSelectedBuyerInvoiceIds(relatedBuyerInvoicesIds)
    }

    const getCheckbox = (row) => {
        return (
            <div> <Checkbox onChange={e => handleSelectedBuyerInvoice(row.buyerInvoiceId)} checked={selectedBuyerInvoiceIds.includes(row.buyerInvoiceId)}></Checkbox>  </div>
        )
    }

    const getInvoiceDocNoBody = (row) => {
        if (row?.docNo){
            let dateString = row["createdAt"] ? getDateString(row["createdAt"]) : "-"
            let navigationLink = `/buyerInvoice/${row["buyerInvoiceId"]}`
            if (row?.isReplaced){
                navigationLink = `/buyerInvoice/${row["buyerInvoice.buyerInvoiceId"]}`
            }
            return (
                <div className="text-center">
                    <div className='flex flex-row justify-content-center' >
                        <p className='doc-no' style={{cursor : "pointer", textDecoration: row.statusId === 6 ? "line-through" : "unset"}} onClick={() => window.open(navigationLink, '_blank')}>{row["docNo"]}</p>
                        <CopyToClipboard text={row.docNo} />
                    </div>
                    <div className="date-string">{dateString}</div>
                </div>
            )
        }
    }

    const getInvoiceItemDocNoBody = (row) => {
        if (row?.docNo){
            let dateString = row["buyerInvoice.createdAt"] ? getDateString(row["buyerInvoice.createdAt"]) : "-"
            let navigationLink = `/buyerInvoice/${row["buyerInvoiceId"]}`
            if (row?.isReplaced){
                navigationLink = `/buyerInvoice/${row["buyerInvoice.buyerInvoiceId"]}`
            }
            return (
                <div className="text-center">
                    <div className='flex flex-row justify-content-center' >
                        <p className='doc-no' style={{cursor : "pointer", textDecoration: row.statusId === 6 ? "line-through" : "unset"}} onClick={() => window.open(navigationLink, '_blank')}>{row["docNo"]}</p>
                        <CopyToClipboard text={row.docNo} />
                    </div>
                    <div className="date-string">{dateString}</div>
                </div>
            )
        }
    }

    const getCreditDocNoBody = (row) => {
        let redirectionLink = row["adjustmentNotes.adjustmentTypeId"] === 1 ? `/credit_note/${row["adjustmentNoteId"]}` : `/buyer-debit-note/${row["adjustmentNoteId"]}`
        if (row?.docNo){
            let dateString = row["adjustmentNotes.createdAt"] ? getDateString(row["adjustmentNotes.createdAt"]) : "-"
            return (
                <div className="text-center">
                    <div className='flex flex-row justify-content-center' >
                        <p className='doc-no' style={{cursor : "pointer", textDecoration: row.statusId === 6 ? "line-through" : "unset"}} onClick={() => window.open(`${redirectionLink}`, '_blank')}>{row["docNo"]}</p>
                        <CopyToClipboard text={row.docNo} />
                    </div>
                    <div className="date-string">{dateString}</div>
                </div>
            )
        }
    }

    const getBuyerPONumbersBody = (row) => {
        if (row.buyerPONumbers){
            return (
                <div className="text-center">
                    <div className='flex flex-row justify-content-center' >
                        <div>{row?.buyerPONumbers.join(", ")}</div>
                    </div>
                </div>
            )
        }
    }

    const getBuyerPONumbersBodyItemLevel = (row) => {
        if (row["buyerInvoice.buyerPONumbers"]){
            return (
                <div className="text-center">
                    <div className='flex flex-column justify-content-center' >
                        <div>{row["buyerInvoice.buyerPONumbers"].join(", ")}</div>
                    </div>
                </div>
            )
        }
    }

    const getCreditNoteBuyerPONumbersBody = (row) => {
        let relatedBuyerInvoiceItem = buyerInvoiceItems?.find(eachBuyerInvoiceItem => eachBuyerInvoiceItem?.buyerInvoiceItemId === row?.buyerInvoiceItemId)
        if (relatedBuyerInvoiceItem?.["buyerInvoice.buyerPONumbers"]){
            return (
                <div className="text-center">
                    <div className='flex flex-row justify-content-center' >
                        <div>{relatedBuyerInvoiceItem?.["buyerInvoice.buyerPONumbers"].join(", ")}</div>
                    </div>
                </div>
            )
        }
    }

    const getPaymentDateBody = (row) => {
        if (row?.paymentDate){
            let dateString = getDateString(row["paymentDate"])
            return (
                <div className="text-center">
                    <div className="">{dateString}</div>
                </div>
            )
        }else {
            return <div> - </div>
        }
    }

    const getGRNBody = (row) => {
        return (
            <div className="text-center">
                <div className="">{row.clientGRN ? "Yes" : "No"}</div>
            </div>
        )
    }

    const getCompanyNameBody = (row) => {
        if(row["company.companyName"]){
            let companyShortName = getCompanyShortName(row["company.companyName"])

            return (
                <div className="text-center">
                    <PMCompanyName companyName={companyShortName} companyId={row.companyId}/>
                </div>
            )
        }
    }

    const getCreditNoteCompanyNameBody = (row) => {
        if(row["adjustmentNotes.companies.companyName"]){
            let companyShortName = getCompanyShortName(row["adjustmentNotes.companies.companyName"])
            let companyId = row["adjustmentNotes.companyId"]

            return (
                <div className="text-center">
                    <PMCompanyName companyName={companyShortName} companyId={companyId}/>
                </div>
            )
        }
    }

    const getCompanyNameBodyItemLevel = (row) => {
        if(row["buyerInvoice.company.companyName"]){
            let companyShortName = getCompanyShortName(row["buyerInvoice.company.companyName"])
            let companyId = row["buyerInvoice.companyId"]

            return (
                <div className="text-center">
                    <PMCompanyName companyName={companyShortName} companyId={companyId}/>
                </div>
            )
        }
    }

    const getCSGSTsBody = (row) => {
        let amount = (row?.itemAmountWithTax - row?.itemAmountBeforeTax)
        return (
            <>
                <div>{row["buyerInvoice.GSTType"] === "CS" ? numberConversion(parseFloat(amount/2), numberLocale) : 0}</div>
            </>
        )
    }

    const getCreditNoteCSGSTsBody = (row) => {
        let itemAmountBeforeTax = parseFloat(row?.adjustedQty) * parseFloat(row?.adjustedPricePerUnit)
        let relatedBuyerInvoiceItem = buyerInvoiceItems?.find(eachBuyerInvoiceItem => eachBuyerInvoiceItem?.buyerInvoiceItemId === row?.buyerInvoiceItemId)
        let gstTax = itemAmountBeforeTax * relatedBuyerInvoiceItem?.GSTRate / 2 * 0.01
        return (
            <>
                <div>{relatedBuyerInvoiceItem?.["buyerInvoice.GSTType"] === "CS" ? numberConversion(parseFloat(gstTax), numberLocale) : 0}</div>
            </>
        )
    }

    const getIGSTsBody = (row) => {
        let amount = numberConversion(parseFloat(row?.itemAmountWithTax - row?.itemAmountBeforeTax), numberLocale)

        let isIGST
        if([3, 4].includes(row["buyerInvoice.supplyTypeId"])){
            isIGST = true
        }

        if(row["buyerInvoice.GSTType"] === "I"){
            isIGST = true
        }

        return (
            <>
                <div>{isIGST ? amount : 0}</div>
            </>
        )
    }

    const getCreditNoteIGSTBody = (row) => {
        let itemAmountBeforeTax = parseFloat(row?.adjustedQty) * parseFloat(row?.adjustedPricePerUnit)
        let relatedBuyerInvoiceItem = buyerInvoiceItems?.find(eachBuyerInvoiceItem => eachBuyerInvoiceItem?.buyerInvoiceItemId === row?.buyerInvoiceItemId)
        let gstTax = itemAmountBeforeTax * relatedBuyerInvoiceItem?.GSTRate * 0.01
        return (
            <>
                <div>{relatedBuyerInvoiceItem?.["buyerInvoice.GSTType"] === "I" ? numberConversion(parseFloat(gstTax), numberLocale) : 0}</div>
            </>
        )
    }

    const getKamNameItemLevel = (row) => {
        let fullName = getFullName(row["buyerInvoice.kamUser.firstName"], row["buyerInvoice.kamUser.lastName"])
        return (
            <>
                <div>{fullName}</div>
            </>
        )
    }

    const getCreditNoteKamNameBody = (row) => {
        let fullName = getFullName(row["adjustmentNotes.users.firstName"], row["adjustmentNotes.users.lastName"])
        return (
            <>
                <div>{fullName}</div>
            </>
        )
    }

    const getCreditNoteUserNameBody = (row) => {
        const relatedBuyerInvoiceItem = buyerInvoiceItems?.find(eachBuyerInvoiceItem => eachBuyerInvoiceItem?.buyerInvoiceItemId === row?.buyerInvoiceItemId)
        let fullName = getFullName(relatedBuyerInvoiceItem?.["buyerInvoice.user.firstName"], relatedBuyerInvoiceItem?.["buyerInvoice.user.lastName"])
        return (
            <>
                <div>{fullName}</div>
            </>
        )
    }

    const getUserNameItemLevel = (row) => {
        let fullName = getFullName(row["buyerInvoice.user.firstName"], row["buyerInvoice.user.lastName"])
        return (
            <>
                <div>{fullName}</div>
            </>
        )
    }

    const getProductNameBody = (row) => {
        const relatedMaterial = materialDetails?.find(eachMaterial => eachMaterial?.materialId === row?.materialId)
        return (
            <>
                <div>{row?.productName ? row?.productName : relatedMaterial?.productName}</div>
            </>
        )
    }

    const getProductSpecificationBody = (row) => {
        const relatedMaterial = materialDetails?.find(eachMaterial => eachMaterial?.materialId === row?.materialId)
        return (
            <>
                <div>{row?.productSpecification ? <Description text={row?.productSpecification} maxLength={100} /> :  <Description text={relatedMaterial?.productSpecification} maxLength={100} />}</div>
            </>
        )
    }

    const getCreditNotesProductNameBody = (row) => {
        const relatedBuyerInvoiceItem = buyerInvoiceItems?.find(eachBuyerInvoiceItem => eachBuyerInvoiceItem?.buyerInvoiceItemId === row?.buyerInvoiceItemId)
        const relatedMaterial = materialDetails?.find(eachMaterial => eachMaterial?.materialId === relatedBuyerInvoiceItem?.materialId)
        return (
            <>
                <div>{relatedMaterial?.productName}</div>
            </>
        )
    }

    const getCreditNotesProductSpecificationBody = (row) => {
        const relatedBuyerInvoiceItem = buyerInvoiceItems?.find(eachBuyerInvoiceItem => eachBuyerInvoiceItem?.buyerInvoiceItemId === row?.buyerInvoiceItemId)
        const relatedMaterial = materialDetails?.find(eachMaterial => eachMaterial?.materialId === relatedBuyerInvoiceItem?.materialId)
        return (
            <>
                {/* <div>{relatedMaterial?.productSpecification}</div> */}
                <Description text={relatedMaterial?.productSpecification} maxLength={100} />
            </>
        )
    }

    const getCreditNotesProductHSNCodeBody = (row) => {
        const relatedBuyerInvoiceItem = buyerInvoiceItems?.find(eachBuyerInvoiceItem => eachBuyerInvoiceItem?.buyerInvoiceItemId === row?.buyerInvoiceItemId)
        return (
            <>
                <div>{relatedBuyerInvoiceItem?.HSNCode}</div>
            </>
        )
    }

    const getProductHSNCodeBody = (row) => {
        const relatedMaterial = materialDetails?.find(eachMaterial => eachMaterial?.materialId === row?.materialId)
        return (
            <>
                <div>{row?.HSNCode}</div>
            </>
        )
    }

    const getCreditNotesUOMBody = (row) => {
        const relatedBuyerInvoiceItem = buyerInvoiceItems?.find(eachBuyerInvoiceItem => eachBuyerInvoiceItem?.buyerInvoiceItemId === row?.buyerInvoiceItemId)
        const relatedMaterial = materialDetails?.find(eachMaterial => eachMaterial?.materialId === relatedBuyerInvoiceItem?.materialId)
        return (
            <>
                <div>{relatedMaterial?.UOM}</div>
            </>
        )
    }

    const getUOMBodyItemLevel = (row) => {
        const relatedMaterial = materialDetails?.find(eachMaterial => eachMaterial?.materialId === row?.materialId)
        return (
            <>
                <div>{row.UOM ? row.UOM : relatedMaterial?.UOM }</div>
            </>
        )
    }

    const getStateBody = (row) => {
        let relatedState = states?.find(eachState => eachState?.stateId === parseInt(row['buyerInvoice.billingAddress.gstinCode'].slice(0,2)))
        return (
            <>
                <div>{relatedState?.stateName}</div>
            </>
        )
    }

    const getCreditNoteStateBody = (row) => {
        const relatedBuyerInvoiceItem = buyerInvoiceItems?.find(eachBuyerInvoiceItem => eachBuyerInvoiceItem?.buyerInvoiceItemId === row?.buyerInvoiceItemId)
        let relatedState = states?.find(eachState => eachState?.stateId === parseInt(relatedBuyerInvoiceItem?.['buyerInvoice.billingAddress.gstinCode']?.slice(0,2)))
        return (
            <>
                <div>{relatedState?.stateName}</div>
            </>
        )
    }

    const getCGSTLedger = (row) => {
        let CGSTLedger = ''
        const relatedPMBillAddress = pmBillAddresses?.find(eachBillAddr => eachBillAddr?.billAddrId === row["buyerInvoice.pmBillAddrId"])
        let relatedPMState = states?.find(eachState => eachState?.stateId === parseInt(relatedPMBillAddress?.gstinCode.slice(0, 2)))
        if (row?.GSTRate && row["buyerInvoice.GSTType"] === "CS") {
            let relatedStateCode = relatedPMState?.stateName === "TAMIL NADU" ? 'TN' : relatedPMState?.stateName.slice(0, 1)
            CGSTLedger = relatedStateCode + " " + "CGST On Output @" + " " + (row.GSTRate/2) + "%"
        }
        else {
            CGSTLedger = '-'
        }

        return (
            <>
                <div>{CGSTLedger}</div>
            </>
        )
    }

    const getCreditNotesCGSTLedger = (row) => {
        let CGSTLedger = ''
        const relatedBuyerInvoiceItem = buyerInvoiceItems?.find(eachBuyerInvoiceItem => eachBuyerInvoiceItem?.buyerInvoiceItemId === row?.buyerInvoiceItemId)
        if (relatedBuyerInvoiceItem?.GSTRate && relatedBuyerInvoiceItem?.["buyerInvoice.GSTType"] === "CS") {
            const relatedPMBillAddress = pmBillAddresses?.find(eachBillAddr => eachBillAddr?.billAddrId === relatedBuyerInvoiceItem?.["buyerInvoice.pmBillAddrId"])
            let relatedPMState = states?.find(eachState => eachState?.stateId === parseInt(relatedPMBillAddress?.gstinCode.slice(0, 2)))
            let relatedStateCode = relatedPMState?.stateName === "TAMIL NADU" ? 'TN' : relatedPMState?.stateName.slice(0, 1)
            CGSTLedger = relatedStateCode + " " + "CGST On Output @" + " " + relatedBuyerInvoiceItem?.GSTRate / 2 + "%"
        }
        else {
            CGSTLedger = '-'
        }

        return (
            <>
                <div>{CGSTLedger}</div>
            </>
        )
    }

    const getSGSTLedger = (row) => {
        let SGSTLedger = ''
        const relatedPMBillAddress = pmBillAddresses?.find(eachBillAddr => eachBillAddr?.billAddrId === row["buyerInvoice.pmBillAddrId"])
        let relatedPMState = states?.find(eachState => eachState?.stateId === parseInt(relatedPMBillAddress?.gstinCode.slice(0, 2)))
        if (row?.GSTRate && row["buyerInvoice.GSTType"] === "CS") {
            let relatedStateCode = relatedPMState?.stateName === "TAMIL NADU" ? 'TN' : relatedPMState?.stateName.slice(0, 1)
            SGSTLedger = relatedStateCode + " " + "SGST On Output @" + " " + (row.GSTRate/2) + "%"
        }
        else {
            SGSTLedger = '-'
        }
        return (
            <>
                <div>{SGSTLedger}</div>
            </>
        )
    }

    const getCreditNotesSGSTLedger = (row) => {
        let SGSTLedger = ''
        const relatedBuyerInvoiceItem = buyerInvoiceItems?.find(eachBuyerInvoiceItem => eachBuyerInvoiceItem?.buyerInvoiceItemId === row?.buyerInvoiceItemId)
        if (relatedBuyerInvoiceItem?.GSTRate && relatedBuyerInvoiceItem?.["buyerInvoice.GSTType"] === "CS") {
            const relatedPMBillAddress = pmBillAddresses?.find(eachBillAddr => eachBillAddr?.billAddrId === relatedBuyerInvoiceItem?.["buyerInvoice.pmBillAddrId"])
            let relatedPMState = states?.find(eachState => eachState?.stateId === parseInt(relatedPMBillAddress?.gstinCode.slice(0, 2)))
            let relatedStateCode = relatedPMState?.stateName === "TAMIL NADU" ? 'TN' : relatedPMState?.stateName.slice(0, 1)
            SGSTLedger = relatedStateCode + " " + "SGST On Output @" + " " + relatedBuyerInvoiceItem?.GSTRate / 2 + "%"
        }
        else {
            SGSTLedger = '-'
        }

        return (
            <>
                <div>{SGSTLedger}</div>
            </>
        )
    }

    const getIGSTLedger = (row) => {
        let IGSTLedger = ''
        const relatedPMBillAddress = pmBillAddresses?.find(eachBillAddr => eachBillAddr?.billAddrId === row["buyerInvoice.pmBillAddrId"])
        let relatedPMState = states?.find(eachState => eachState?.stateId === parseInt(relatedPMBillAddress?.gstinCode.slice(0, 2)))

        let isIGST
        if([3, 4].includes(row["buyerInvoice.supplyTypeId"])){
            isIGST = true
        }

        if(row["buyerInvoice.GSTType"] === "I"){
            isIGST = true
        }

        if (row?.GSTRate && isIGST) {
            let relatedStateCode = relatedPMState?.stateName === "TAMIL NADU" ? 'TN' : relatedPMState?.stateName.slice(0, 1)
            IGSTLedger = relatedStateCode + " " + "IGST On Output @" + " " + (row.GSTRate) + "%"
        }
        else {
            IGSTLedger = '-'
        }
        return (
            <>
                <div>{IGSTLedger}</div>
            </>
        )
    }

    const getCreditNotesIGSTLedger = (row) => {
        let IGSTLedger = ''
        const relatedBuyerInvoiceItem = buyerInvoiceItems?.find(eachBuyerInvoiceItem => eachBuyerInvoiceItem?.buyerInvoiceItemId === row?.buyerInvoiceItemId)
        if (relatedBuyerInvoiceItem?.GSTRate && relatedBuyerInvoiceItem?.["buyerInvoice.GSTType"] === "I") {
            const relatedPMBillAddress = pmBillAddresses?.find(eachBillAddr => eachBillAddr?.billAddrId === relatedBuyerInvoiceItem?.["buyerInvoice.pmBillAddrId"])
            let relatedPMState = states?.find(eachState => eachState?.stateId === parseInt(relatedPMBillAddress?.gstinCode.slice(0, 2)))
            let relatedStateCode = relatedPMState?.stateName === "TAMIL NADU" ? 'TN' : relatedPMState?.stateName.slice(0, 1)
            IGSTLedger = relatedStateCode + " " + "IGST On Output @" + " " + relatedBuyerInvoiceItem?.GSTRate + "%"
        }
        else {
            IGSTLedger = '-'
        }

        return (
            <>
                <div>{IGSTLedger}</div>
            </>
        )
    }

    const getPurchaseLedger = (row) => {
        let purchaseLedger = ''
        let sellerState = ''
        let procmartState = ''
        if(!row.GSTRate){
            if (row["buyerInvoice.billingAddress.gstinCode"]) {
                let relatedSellerState = states?.find(eachState => eachState?.stateId === parseInt(row["buyerInvoice.billingAddress.gstinCode"].slice(0,2)))
                sellerState = relatedSellerState?.stateName
            }
            if (row["buyerInvoice.pmBillAddrId"]) {
                const relatedPMBillAddress = pmBillAddresses?.find(eachBillAddr => eachBillAddr?.billAddrId === row["buyerInvoice.pmBillAddrId"])
                let relatedPMState = states?.find(eachState => eachState?.stateId === parseInt(relatedPMBillAddress?.gstinCode.slice(0, 2)))
                if(relatedPMState?.stateId){
                    procmartState = relatedPMState?.stateName
                }
            }

            if(sellerState.toUpperCase() != procmartState.toUpperCase()){
                purchaseLedger = "IGST Sales @ 0 %"
            }
            else{
                if (row.HSNCode.toString().startsWith('99')) {
                    purchaseLedger = "Basic Sales @" + " " + (row.GSTRate) + "%" 
                    if(row.GSTRate){
                        purchaseLedger += "_Services"
                    }
                }
                else {
                    purchaseLedger = "Basic Sales @" + " " + (row.GSTRate) + "%" 
                    if(row.GSTRate){
                        purchaseLedger += "_Goods"
                    }
                }
            }
        }else{
            if (row?.GSTRate && row["buyerInvoice.GSTType"] === "I") {
                if (row?.HSNCode.toString().startsWith('99')) {
                    purchaseLedger = "IGST Sales @" + " " + row?.GSTRate + "%" 
                    if(row.GSTRate){
                        purchaseLedger += "_Services"
                    }
                }
                else {
                    purchaseLedger = "IGST Sales @" + " " + row?.GSTRate + "%" 
                    if(row.GSTRate){
                        purchaseLedger += "_Goods"
                    }
                }
            }
            else {
                if (row?.HSNCode.toString().startsWith('99')) {
                    purchaseLedger = "Basic Sales @" + " " + (row?.GSTRate) + "%" 
                    if(row.GSTRate){
                        purchaseLedger += "_Services"
                    }
                }
                else {
                    purchaseLedger = "Basic Sales @" + " " + (row?.GSTRate) + "%" 
                    if(row.GSTRate){
                        purchaseLedger += "_Goods"
                    }
                }
            }
        }

        return (
            <>
                <div>{purchaseLedger}</div>
            </>
        )
    }

    const getCreditNotePurchaseLedger = (row) => {
        let purchaseLedger = ''
        let relatedBuyerInvoiceItem = buyerInvoiceItems?.find(eachBuyerInvoiceItem => eachBuyerInvoiceItem?.buyerInvoiceItemId === row?.buyerInvoiceItemId)
        if (relatedBuyerInvoiceItem?.GSTRate && relatedBuyerInvoiceItem?.["buyerInvoice.GSTType"] === "I") {
            if (relatedBuyerInvoiceItem?.HSNCode?.toString().startsWith('99')) {
                purchaseLedger = "IGST Sales @" + " " + (relatedBuyerInvoiceItem?.GSTRate ? relatedBuyerInvoiceItem?.GSTRate : 0) + "%" 
                if(relatedBuyerInvoiceItem.GSTRate){
                    purchaseLedger += "_Services"
                }
            }
            else {
                purchaseLedger = "IGST Sales @" + " " + (relatedBuyerInvoiceItem?.GSTRate ? relatedBuyerInvoiceItem?.GSTRate : 0) + "%" 
                if(relatedBuyerInvoiceItem?.GSTRate){
                    purchaseLedger += "_Goods"
                }
            }
        }
        else {
            if (relatedBuyerInvoiceItem?.HSNCode.toString().startsWith('99')) {
                purchaseLedger = "Basic Sales @" + " " + (relatedBuyerInvoiceItem?.GSTRate ? relatedBuyerInvoiceItem?.GSTRate : 0) + "%" 
                if(relatedBuyerInvoiceItem.GSTRate){
                    purchaseLedger += "_Services"
                }
            }
            else {
                purchaseLedger = "Basic Sales @" + " " + (relatedBuyerInvoiceItem?.GSTRate ? relatedBuyerInvoiceItem?.GSTRate : 0) + "%" 
                if(relatedBuyerInvoiceItem?.GSTRate){
                    purchaseLedger += "_Goods"
                }
            }
        }

        return (
            <>
                <div>{purchaseLedger}</div>
            </>
        )
    }

    const getCostCenterBody = (row) => {
        const relatedPMBillAddress = pmBillAddresses?.find(eachPMBillAddress => eachPMBillAddress?.billAddrId === row["buyerInvoice.pmBillAddrId"])
        const relatedState = states?.find(eachState => eachState?.stateId === parseInt(relatedPMBillAddress?.gstinCode.slice(0, 2)))
        return (
            <>
                <div>{relatedState?.stateName}</div>
            </>
        )
    }

    const getBuyerInvoiceAmountsBody = (row, rowType) => {
        const relatedDocReplacementLogItems = docReplacementLogItems.filter(eachDocReplacementLogItem => eachDocReplacementLogItem?.docId === row?.buyerInvoiceId)
        const relatedBuyerInvoiceItems = buyerInvoiceItems.filter(eachBuyerInvoiceItem => eachBuyerInvoiceItem?.buyerInvoiceId === row?.buyerInvoiceId)

        let totalInvTax = 0
        let totalInvAmount = 0
        if (relatedDocReplacementLogItems.length){
            const updatedBuyerInvoiceItems = generateReplacedProducts(relatedBuyerInvoiceItems, relatedDocReplacementLogItems)

            updatedBuyerInvoiceItems.map(eachBuyerInvoiceItem => {
                let itemTaxAmount = eachBuyerInvoiceItem.qty * eachBuyerInvoiceItem.unitPrice * eachBuyerInvoiceItem.GSTRate * 0.01
                totalInvTax += itemTaxAmount
            })
            totalInvTax = round(totalInvTax, 2)
            totalInvAmount = round(parseFloat(row.totalAmountBeforeTax) + totalInvTax, 2)
        }else {
            totalInvTax = row?.totalInvTax
            totalInvAmount = row?.totalInvAmount
        }

        if (rowType === "CSGSTAmounts"){
            if (row?.GSTType === "CS"){
                return <div> {numberConversion(totalInvTax/ 2, numberLocale)} </div>
            }
            return <div>0</div>
        }else if (rowType === "IGSTAmounts") {
            if (row?.GSTType === "I"){
                return <div> {numberConversion(totalInvTax, numberLocale)} </div>
            }
            return <div>0</div>
        }else if (rowType === "totalAmount"){
            return <div>{numberConversion(totalInvAmount, numberLocale)}</div>
        }
    }


    const handleTableColumn = (searchParams) => {

        const dataType = searchParams.dataType

        switch(dataType) {

            case "buyerInvoice" :
                setTableColumns([...tableColumnsDefault])
                break

            case "accountInvoice" :
                setTableColumns(tableColumnsAccounts)
                break

            case "productsInvoice" :
                setTableColumns(tableColumnsProducts)
                break

            case "salesInvoice" :
                setTableColumns(tableColumnsSales)
                break
            case "creditNotesData":
                setTableColumns(tableColumnsCreditNotes)
                break

        }
    }

    const tableColumnsDefault = [
        {
            header: <div> <Checkbox onChange={e => selectAllBuyerInvoices(e)} checked={ buyerInvoices?.length == 0 ? false : selectedBuyerInvoiceIds.length == buyerInvoices?.length}></Checkbox>  </div>,
            field: 'checkbox',
            body:  getCheckbox,
            style:{ minWidth: '60px'}
        },
        {
            header: 'Invoice No',
            field: '',
            body: getInvoiceDocNoBody,
        },
        {
            header: 'KAM Name',
            field: '',
            body : getKamName,
            style:{ minWidth: '150px'}
        },
        {
            header: 'Company Name',
            field: '',
            body: getCompanyNameBody,
            style:{ minWidth: '150px'}
        },
        {
            header: 'User Name',
            field: '',
            body : getUserName,
            style:{ minWidth: '150px'}
        },
        {
            header: 'GSTIN',
            field: 'billingAddress.gstinCode',
        },
        {
            header: 'BuyerPO Nos',
            field: '',
            body: getBuyerPONumbersBody,
            style:{ minWidth: '80px'}
        },
        
        {
            header: 'E-Way Bill No',
            field: 'ewaybill.ewayBillNo',
            style:{ minWidth: '120px'}
        },
        {
            header: 'Basic Price',
            field: 'totalAmountBeforeTax - logisticsBeforeTax',
            body : getBasicPrice,
            style:{ minWidth: '110px'},
        },
        {
            header: 'Logistic Charge',
            field: 'logisticsBeforeTax',
            style:{ minWidth: '70px'},
        },
        {
            header: 'CGST Amount',
            field: 'logisticsBeforeTax',
            body: (row) => getBuyerInvoiceAmountsBody(row, "CSGSTAmounts"),
            style:{ minWidth: '70px'},
        },
        {
            header: 'SGST Amount',
            field: 'logisticsBeforeTax',
            body: (row) => getBuyerInvoiceAmountsBody(row, "CSGSTAmounts"),
            style:{ minWidth: '70px'},
        },
        {
            header: 'IGST Amount',
            field: 'logisticsBeforeTax',
            body: (row) => getBuyerInvoiceAmountsBody(row, "IGSTAmounts"),
            style:{ minWidth: '70px'},
        },
        {
            header: 'Total Price',
            field: 'totalInvAmount',
            body: (row) => getBuyerInvoiceAmountsBody(row, "totalAmount"),
            style:{ minWidth: '110px'},
        },
        {
            header: 'Order Type',
            field: 'orderType',
            style: { minWidth: "150px"}
        },
        {
            header: 'GRN',
            field: '',
            body: getGRNBody,
        },
        {
            header: 'Payment Date',
            field: '',
            body: getPaymentDateBody, 
            style: { minWidth: "120px"}
        },
    ]

    let [ tableColumns, setTableColumns ] = useState(tableColumnsDefault)

    const tableColumnsAccounts = [
        {
            header: <div> <Checkbox onChange={e => selectAllBuyerInvoices()} checked={buyerInvoices?.length == 0 ? false : selectedBuyerInvoiceIds.length == buyerInvoices?.length}></Checkbox>  </div>,
            field: 'checkbox',
            body:  getCheckbox,
            style:{ minWidth: '60px'}
        },
        {
            header: 'Invoice No',
            field: '',
            body: getInvoiceDocNoBody,
        },
        {
            header: 'KAM Name',
            field: '',
            body : getKamName,
            style:{ minWidth: '150px'}
        },
        {
            header: 'Company Name',
            field: 'company.companyName',
            style:{ minWidth: '200px'}
        },
        {
            header: 'User Name',
            field: '',
            body : getUserName,
            style:{ minWidth: '150px'}
        },
        {
            header: 'GSTIN',
            field: 'billingAddress.gstinCode',
        },
        {
            header: 'BuyerPO Nos',
            field: '',
            body: getBuyerPONumbersBody,
            style:{ minWidth: '80px'}
        },
        /*{
            header: 'E-Way Bill No',
            field: 'ewaybill.ewayBillNo',
        },*/
        {
            header: 'Basic Price',
            field: 'totalAmountBeforeTax - logisticsBeforeTax',
            body : getBasicPrice,
            style:{ minWidth: '110px'},
        },
        {
            header: 'Logistic Charge',
            field: 'logisticsBeforeTax',
            style:{ minWidth: '70px'},
        },
        {
            header: 'CGST Amount',
            field: 'logisticsBeforeTax',
            body : (row) => {
                if(row.GSTType == "CS"){
                    return <div> {numberConversion(parseFloat(row.totalInvTax/ 2), numberLocale)} </div>
                }
                else {
                    return <div> 0 </div>
                }
            },
            style:{ minWidth: '70px'},
        },
        {
            header: 'SGST Amount',
            field: 'logisticsBeforeTax',
            body : (row) => {
                if(row.GSTType == "CS"){
                    return <div> {numberConversion(parseFloat(row.totalInvTax/ 2), numberLocale)} </div>
                }
                else {
                    return <div> 0 </div>
                }
            },
            style:{ minWidth: '70px'},
        },
        {
            header: 'IGST Amount',
            field: 'logisticsBeforeTax',
            body : (row) => {
                if(row.GSTType == "I"){
                    return <div> {numberConversion(parseFloat(row.totalInvTax), numberLocale)} </div>
                }
                else {
                    return <div> 0 </div>
                }
            },
            style:{ minWidth: '70px'},
        },
        {
            header: 'Total Price',
            field: 'totalInvAmount',
            style:{ minWidth: '70px'},
            body : (row) => {
                return numberConversion(parseFloat(row.totalInvAmount), numberLocale)
            }
        },
        {
            header: 'Type',
            field: 'orderType',
            style:{ minWidth: '70px'},
        },
    ]

    const tableColumnsProducts = [
        {
            header: <div> <Checkbox onChange={e => selectAllBuyerInvoices()} checked={buyerInvoices?.length == 0 ? false : selectedBuyerInvoiceIds.length == buyerInvoices?.length}></Checkbox>  </div>,
            field: 'checkbox',
            body:  getCheckbox,
            style:{ minWidth: '60px'}
        },
        {
            header: 'Invoice No',
            field: '',
            body: getInvoiceDocNoBody
        },
        {
            header: 'KAM Name',
            field: '',
            body : getKamNameItemLevel,
            style:{ minWidth: '150px'}
        },
        {
            header: 'Company Name',
            field: 'company.companyName',
            body: getCompanyNameBodyItemLevel,
            style:{ minWidth: '180px'}
        },
        {
            header: 'User Name',
            field: '',
            body : getUserNameItemLevel,
            style:{ minWidth: '100px'}
        },
        {
            header: 'BuyerPO Nos',
            field: '',
            body: getBuyerPONumbersBodyItemLevel,
            style:{ width: '100px'}
        },
        {
            header: 'Buyer PO Date',
            field: '',
            body : getBuyerPODateBodyItemLevel,
            style:{ minWidth: '100px'}

        },
        {
            header: 'GSTIN',
            field: 'buyerInvoice.billingAddress.gstinCode',
        },
        {
            header: 'Products',
            // field: 'buyerInvoiceItems.materialDetails.productName',
            body: getProductNameBody,
            style:{ minWidth: '150px'}
        },
        {
            header: 'HSN',
            field: 'buyerInvoiceItems.materialDetails.HSNCode',
            body: getProductHSNCodeBody,
            style:{ minWidth: '60px'}
        },
        {
            header: 'Specifications',
            // field: 'buyerInvoiceItems.materialDetails.productSpecification',
            body: getProductSpecificationBody,
            style:{ minWidth: '250px'}
        },
        /*
        {
            header: 'Industry',
            field: 'category',
            style:{ minWidth: '120px'}
        },
        {
            header: 'Company ERP Code',
            field: 'companyERPCode',
            style:{ minWidth: '150px'}
        },
        */
        {
            header: 'Quantity',
            field: 'qty',
        },
        {
            header: 'Unit',
            field: '',
            body: getUOMBodyItemLevel
        },
        {
            header: 'Basic Price',
            field: 'totalAmountBeforeTax - logisticsBeforeTax',
            body : getBasicPriceItemLevel,
            style:{ minWidth: '110px'},
        },
        {
            header: 'Logistic Charge',
            field: 'buyerInvoice.logisticsBeforeTax',
            style:{ minWidth: '70px'},
        },
        {
            header: 'CGST Amount',
            field: 'logisticsBeforeTax',
            body : getCSGSTsBody,
            style:{ minWidth: '70px'},
        },
        {
            header: 'SGST Amount',
            field: 'logisticsBeforeTax',
            body : getCSGSTsBody,
            style:{ minWidth: '70px'},
        },
        {
            header: 'IGST Amount',
            field: 'logisticsBeforeTax',
            body : getIGSTsBody,
            style:{ minWidth: '70px'},
        },
        {
            header: 'Total Price',
            field: 'itemAmountWithTax',
            style:{ minWidth: '70px'},
            body : (row) => {
                return numberConversion(parseFloat(row.itemAmountWithTax), numberLocale)
            }
        },
        {
            header: 'Type',
            field: 'orderType',
        },
    ]


    const tableColumnsSales = [
        {
            header: <div> <Checkbox onChange={e => selectAllBuyerInvoices()} checked={buyerInvoices?.length == 0 ? false : selectedBuyerInvoiceIds.length == buyerInvoices?.length}></Checkbox>  </div>,
            field: 'checkbox',
            body:  getCheckbox,
            style:{ minWidth: '60px'}
        },
        {
            header: 'Invoice No',
            field: '',
            body: getInvoiceItemDocNoBody
        },
        {
            header: 'BASE-VCH-TYPE',
            field: 'buyerInvoiceId',
            body : (row) => {
                return <div> Sales </div>
            },
            style:{ minWidth: '100px' }
        },
        {
            header: 'VCH-NO',
            field: 'docNo',
            style:{ minWidth: '100px' }
        },
        {
            header: 'Company Name',
            field: 'company.companyName',
            body: getCompanyNameBodyItemLevel,
            style:{ minWidth: '150px'}
        },
        {
            header: 'KAM Name',
            field: '',
            body : getKamNameItemLevel,
            style:{ minWidth: '150px'}
        },
        {
            header: 'User Name',
            field: '',
            body : getUserNameItemLevel,
            style:{ minWidth: '150px'}
        },
        {
            header: 'GSTIN',
            field: 'buyerInvoice.billingAddress.gstinCode',
        },
        {
            header: 'State',
            field: '',
            body: getStateBody,
            style:{ minWidth: '110px' }
        },
        {
            header: 'BuyerPO Nos',
            field: '',
            body: getBuyerPONumbersBodyItemLevel,
            style:{ minWidth: '120px' }
        },
        {
            header: 'Products',
            // field: 'buyerInvoiceItems.materialDetails.productName',
            body: getProductNameBody,
            style:{ minWidth: '150px'}
        },
        {
            header: 'HSN',
            field: 'buyerInvoiceItems.materialDetails.HSNCode',
            body: getProductHSNCodeBody,
        },
        {
            header: 'Specifications',
            // field: 'buyerInvoiceItems.materialDetails.productSpecification',
            body: getProductSpecificationBody,
            style:{ minWidth: '250px'}
        },
        {
            header: 'Quantity',
            field: 'qty',
        },
        {
            header: 'Unit',
            field: '',
            body: getUOMBodyItemLevel
        },
        {
            header: 'Unit Price',
            field: 'unitPrice',
            style:{ minWidth: '80px'},
            body: (row) => {
                return numberConversion(parseFloat(row.unitPrice), numberLocale)
            }
        },
        {
            header: 'Basic Price',
            field: 'totalAmountBeforeTax - logisticsBeforeTax',
            body : getBasicPriceItemLevel,
            style:{ minWidth: '80px'},
        },
        {
            header: 'Logistic Charge',
            field: 'buyerInvoice.logisticsBeforeTax',
            style:{ minWidth: '70px'},
        },
        {
            header: 'CGST Ledger',
            field: 'logisticsBeforeTax',
            body: getCGSTLedger,
            style: {minWidth: "180px"}
        },
        {
            header: 'CGST Amount',
            field: '',
            body : getCSGSTsBody,
            style:{ minWidth: '90px'},
        },
        {
            header: 'SGST Ledger',
            field: '',
            body: getSGSTLedger,
            style: {minWidth: "180px"}
        },
        {
            header: 'SGST Amount',
            field: 'logisticsBeforeTax',
            body : getCSGSTsBody,
            style:{ minWidth: '90px'},
        },
        {
            header: 'IGST Ledger',
            field: '',
            body: getIGSTLedger,
            style: {minWidth: "180px"}
        },
        {
            header: 'IGST Amount',
            field: 'logisticsBeforeTax',
            body : getIGSTsBody,
            style:{ minWidth: '80px'},
        },
        {
            header: 'Total Price',
            field: 'itemAmountWithTax',
            style:{ minWidth: '90px'},
            body: (row) => {
                return numberConversion(parseFloat(row.itemAmountWithTax), numberLocale)
            }
        },
        {
            header: 'Purchase Ledger',
            field: '',
            body: getPurchaseLedger,
            style: {minWidth: "210px"}
        },
        {
            header: 'Cost Center',
            field: '',
            body: getCostCenterBody,
            style:{ minWidth: '100px'}
        },
    ]

    const tableColumnsCreditNotes = [
        // {
        //     header: <div> <Checkbox onChange={e => selectAllBuyerInvoices()} checked={buyerInvoices?.length == 0 ? false : selectedBuyerInvoiceIds.length == buyerInvoices?.length}></Checkbox>  </div>,
        //     field: 'checkbox',
        //     body:  getCheckbox
        // },
        {
            header: 'Credit Note No',
            field: '',
            style: {minWidth: '120px'},
            body: getCreditDocNoBody
        },
        {
            header: 'BASE-VCH-TYPE',
            field: 'buyerInvoiceId',
            body : (row) => {
                return <div> Sales Inward </div>
            },
            style:{ minWidth: '130px' }
        },
        {
            header: 'VCH-NO',
            field: 'docNo',
            style:{ minWidth: '80px' }
        },
        {
            header: 'Company Name',
            field: 'company.companyName',
            body: getCreditNoteCompanyNameBody,
            style:{ minWidth: '160px'}
        },
        {
            header: 'KAM Name',
            field: '',
            body : getCreditNoteKamNameBody,
            style:{ minWidth: '120px'}
        },
        {
            header: 'User Name',
            field: '',
            body : getCreditNoteUserNameBody,
            style:{ minWidth: '150px'}
        },
        {
            header: 'GSTIN',
            field: 'buyerInvoice.billingAddress.gstinCode',
            body: getCreditNoteGSTINBody,
            style: {minWidth: '100px'}
        },
        {
            header: 'State',
            field: '',
            body: getCreditNoteStateBody,
            style: {minWidth: '100px'}
        },
        {
            header: 'BuyerPO Nos',
            field: '',
            body: getCreditNoteBuyerPONumbersBody,
            style:{ minWidth: '150px' }
        },
        {
            header: 'Products',
            body: getCreditNotesProductNameBody,
            style:{ minWidth: '150px'}
        },
        {
            header: 'HSN',
            field: 'buyerInvoiceItems.materialDetails.HSNCode',
            body: getCreditNotesProductHSNCodeBody,
        },
        {
            header: 'Specifications',
            body: getCreditNotesProductSpecificationBody,
            style:{ minWidth: '230px'}
        },
        {
            header: 'Quantity',
            field: 'adjustedQty',
        },
        {
            header: 'Unit',
            field: '',
            body: getCreditNotesUOMBody
        },
        {
            header: 'Unit Price',
            field: 'adjustedPricePerUnit',
            style:{ minWidth: '90px'},
            body: (row) => {
                return numberConversion(parseFloat(row.adjustedPricePerUnit), numberLocale)
            }
        },
        {
            header: 'Basic Price',
            field: 'totalAmountBeforeTax - logisticsBeforeTax',
            body : getCreditNotesBasicPriceBody,
            style:{ minWidth: '90px'},
        },
        {
            header: 'Logistic Charge',
            field: 'buyerInvoice.logisticsBeforeTax',
            style:{ minWidth: '90px'},
        },
        {
            header: 'CGST Ledger',
            field: 'logisticsBeforeTax',
            body: getCreditNotesCGSTLedger,
            style: {minWidth: "180px"}
        },
        {
            header: 'CGST Amount',
            field: '',
            body : getCreditNoteCSGSTsBody,
            style:{ minWidth: '90px'},
        },
        {
            header: 'SGST Ledger',
            field: '',
            body: getCreditNotesSGSTLedger,
            style: {minWidth: "180px"}
        },
        {
            header: 'SGST Amount',
            field: 'logisticsBeforeTax',
            body : getCreditNoteCSGSTsBody,
            style:{ minWidth: '90px'},
        },
        {
            header: 'IGST Ledger',
            field: '',
            body: getCreditNotesIGSTLedger,
            style: {minWidth: "180px"}
        },
        {
            header: 'IGST Amount',
            field: 'logisticsBeforeTax',
            body : getCreditNoteIGSTBody,
            style:{ minWidth: '90px'},
        },
        {
            header: 'Total Price',
            field: 'itemAmountWithTax',
            body: getCreditNotesTotalAmountBody,
            style:{ minWidth: '90px'},
        },
        {
            header: 'Purchase Ledger',
            field: '',
            body: getCreditNotePurchaseLedger,
            style: {minWidth: "220px"}
        },
        {
            header: 'Cost Center',
            field: '',
            body: getCreditNoteProcmartStateBody
        },
    ]

    const handlePaymentConfirmationDate = (event) => {
        setPaymentConfirmationDate(event.target.value)
        setGRNUpdateObj({ ...GRNUpdateObj , GRNDate:event.target.value })
    }

    const handleGRNRemarksChange = (event) => {
        setGRNUpdateObj({ ...GRNUpdateObj , remarks: event.target.value })
    }

    const handleGRNOptions = (event) => {
        const GRNFlag = event.value == "Yes" ? true : false
        setGRNUpdateObj({ ...GRNUpdateObj, GRNDate: GRNFlag ? GRNUpdateObj.GRNDate : null, GRNFlag: GRNFlag, GRNValue : event.value  })
    }

    const handleSearchTabDisplay = () => {
        if (!isSearchPinned) {
          dispatch(setIsSearchVisible(!isSearchVisible))
        }
    }

    const GRNModalFooterContent = () => {
        return (
            <div className='flex justify-content-center align-items-center mt-5'>
                <Button  label="Submit" onClick={(e) => submitGRN()}></Button>
            </div>
        )
    }

    const paymentDateModalFooterContent = () => {
        return (
            <div className='flex justify-content-center align-items-center mt-5'>
                <Button label="Submit" onClick={(e) => submitPaymentConfirmationDate()}></Button>
            </div>
        )
    }

    const submitPaymentConfirmationDate = async () => {
        if(!paymentConfirmationDate){
            return dispatch(raiseToast({ severity: 'warn', detail: "Please select payment confirmation date", life: 3000 }))
        }
        const updateObj = {
            paymentConfirmationDate : paymentConfirmationDate,
            buyerInvoiceIds : selectedBuyerInvoiceIds
        }

        if (window.confirm("Are you sure you want to upate payment confirmation date")) {
            setIsLoading(true)
            const updatePaymentConfirmationDateResponse = await updatePaymentConfirmationDate(updateObj)
            
            setIsLoading(false)
            if (updatePaymentConfirmationDateResponse.failure) {
                return dispatch(raiseToast({ severity: 'error', detail: updatePaymentConfirmationDateResponse.data.error, life: 3000 }))
            } else {

                let updatedBuyerInvoices = buyerInvoices.map(eachBuyerInvoice => {
                    if (selectedBuyerInvoiceIds.includes(eachBuyerInvoice.buyerInvoiceId)) {
                        return {
                            ...eachBuyerInvoice,
                            paymentDate: paymentConfirmationDate
                        }
                    }
                    return eachBuyerInvoice
                })

                dispatch(updateBuyerInvoiceData(updatedBuyerInvoices))

                setSelectedBuyerInvoiceIds([])
                setPaymentConfirmationDate(null)
                setPaymentModalVisible(false)
                return dispatch(raiseToast({ severity: 'success', detail: "Payment date updated successfully", life: 3000 }))
            }
        }
    }

    const submitGRN = async () => {
        if(!GRNUpdateObj.GRNValue){
            return dispatch(raiseToast({ severity: 'warn', detail: "Please select GRN status", life: 3000 }))
        }

        else if(GRNUpdateObj.GRNValue === "Yes" && !GRNUpdateObj.GRNDate){
            return dispatch(raiseToast({ severity: 'warn', detail: "Please select GRN date", life: 3000 }))
        }

        else if(!GRNUpdateObj.remarks){
            return dispatch(raiseToast({ severity: 'warn', detail: "Please add GRN update remarks", life: 3000 }))
        }

        const updateObj = {
            clientGRN : GRNUpdateObj.GRNValue,
            clientGRNDate : GRNUpdateObj.GRNDate,
            clientGRNRemarks : GRNUpdateObj.remarks,
            buyerInvoiceIds : selectedBuyerInvoiceIds
        }

        if (window.confirm("Are you sure you want to upate GRN")) {
            setIsLoading(true)
            const updateGRNResponse = await updateGRN(updateObj)
            console.log("updateGRN response ===>", updateGRNResponse)
            setIsLoading(false)

            if (updateGRNResponse.failure) {
                return dispatch(raiseToast({ severity: 'error', detail: updateGRNResponse.data.error, life: 3000 }))
            } else {

                let updatedBuyerInvoices = buyerInvoices.map(eachBuyerInvoice => {
                    if (selectedBuyerInvoiceIds.includes(eachBuyerInvoice.buyerInvoiceId)) {
                        return {
                            ...eachBuyerInvoice,
                            clientGRN: GRNUpdateObj.GRNValue,
                            clientGRNDate: GRNUpdateObj.GRNDate,
                            clientGRNRemarks: GRNUpdateObj.remarks,
                        }
                    }
                    return eachBuyerInvoice
                })

                dispatch(updateBuyerInvoiceData(updatedBuyerInvoices)) 
                setSelectedBuyerInvoiceIds([])
                setGRNModalVisible(false)
                setGRNUpdateObj({ GRNValue: null, GRNDate: null, remarks: null, })
                return dispatch(raiseToast({ severity: 'success', detail: "GRN updated successfully", life: 3000 }))
            }
        }
    }

    const incrementPageNo = async (e) => {
        dispatch(updatePaginationParams({...paginationParams, pageNo: e.page, recordsPerPage: e.rows }))
    }

    const actionButtons = [
        {
            label: "Action",
            command: (event) => actionMenu.current?.toggle(event),
        },
        {
            icon: "pi pi-filter",
            label: "Filter",
            command: () => handleSearchTabDisplay(),
            className: isSearchActive ? "active-group-button" : ""
        },
        {
            icon: isSearchPinned ? "pi pi-lock" : "pi pi-lock-open",
            command: () => toggleSearchPin() 
        }
    ]

    let displaySearchBox = isSearchPinned || isHoveringSearchZone || isSearchVisible

    return (
        <div className="flex flex-column"> 
            <div
                onMouseEnter={() => {
                    if (!isSearchPinned) {
                        setIsHoveringSearchZone(true)
                        setHasEverHoveredRightEdge(true)
                        dispatch(setIsSearchVisible(true))
                    }
                }}
                style={{
                    position: 'fixed',
                    top: 0,
                    right: 0,
                    width: '8px',
                    height: '100vh',
                    zIndex: 999
                }}
            />
            <div className="flex flex-column md:flex-row md:justify-content-between">
                <h4 className="mx-2 my-1 md:m-2">ADMIN ACCOUNTS BUYER</h4> 
                
                <div className="flex gap-2 mb-2 justify-content-end req-action-Body">
                    { ["buyerInvoice", "accountInvoice"].includes(searchParams.dataType) ? 
                        <div className="flex justify-content-center align-items-center">
                            Total Basic Price: <span className=' ml-2 text-green-600'> {totalBasicAmount ? numberConversion(totalBasicAmount, numberLocale) : 0} </span>
                        </div> : null
                    }
                    <Menu model={actionMenuItems} popup menuRef={actionMenu} />
                    <ButtonGroup buttons={actionButtons} />
                </div>
            </div>
            
            <div className='flex flex-row'>
                <div className="admin-account-buyer-table" style={{ order: "1",  width: displaySearchBox && !isMobileDevice ? "82%" : "100%", maxHeight: "'calc(100%-100px)'" }}>
                    <div className='p-card mb-0'>
                        <div className="flex-grow-1 petty-cash-table">
                            <Table
                                //lazy
                                page={paginationParams.pageNo}
                                onPageChange={(e) => incrementPageNo(e)}
                                tableColumns={tableColumns}
                                data={dataType === "creditNotesData" ? adjustmentNoteItems : buyerInvoices}
                                rows={paginationParams.recordsPerPage}
                                scrollable={"true"}
                                scrollHeight={"550px"}
                                paginator
                                totalRecords={paginationParams.totalRecords}
                                excessPagesFound={false}
                                loading={isPageLoading}
                            />
                        </div>
                    </div>
                </div>
                {displaySearchBox && !isMobileDevice && (
                    <div
                        style={{ order: "2", width: "18%", paddingLeft: "10px" }} className="justify-content-end"
                        onMouseLeave={(e) => {
                            const bounds = e.currentTarget.getBoundingClientRect()
                            const leftExit = e.clientX < bounds.left
                            const topExit = e.clientY < bounds.top
                            const bottomExit = e.clientY > bounds.bottom

                            if (!isSearchPinned && (leftExit || topExit || bottomExit)) {
                                setIsHoveringSearchZone(false)
                                setHasEverHoveredRightEdge(false)
                            }
                        }}

                        onMouseEnter={() => {
                            if (!isSearchPinned) {
                                setIsHoveringSearchZone(true)
                            }
                        }}
                    >
                        <Search  handleTableColumn={handleTableColumn} setSelectedBuyerInvoiceIds={setSelectedBuyerInvoiceIds} buyerInvoices={buyerInvoices} setAdjustmentNoteItems={setAdjustmentNoteItems} setDataType={setDataType} materialDetails={materialDetails}/>
                    </div>
                )}
                {/* {
                    isSearchVisible && !isMobileDevice &&
                    <div style={{ order: "2", width: "18%", paddingLeft: "10px" }} className="justify-content-end ">
                        <Search  handleTableColumn={handleTableColumn} setSelectedBuyerInvoiceIds={setSelectedBuyerInvoiceIds} buyerInvoices={buyerInvoices} setAdjustmentNoteItems={setAdjustmentNoteItems} setDataType={setDataType} materialDetails={materialDetails}/>
                    </div>
                } */}

                {
                    isSearchVisible && isMobileDevice && 
                    <div style={{ order: "2", width: "18%", paddingLeft: "10px" }} className="justify-content-end ">
                        <Sidebar position='right' visible={isSearchVisible} onHide={() => dispatch(setIsSearchVisible(false))}>
                            <Search  handleTableColumn={handleTableColumn} setSelectedBuyerInvoiceIds={setSelectedBuyerInvoiceIds} buyerInvoices={buyerInvoices} setAdjustmentNoteItems={setAdjustmentNoteItems} setDataType={setDataType} materialDetails={materialDetails}/>
                        </Sidebar>
                    </div>
                }
            </div>
            <div>
                <Dialog header="Update Payment Confirmation Date" visible={paymentDateModalVisible} style={{ width: '45vw', minHeight: "35%" }} footer={paymentDateModalFooterContent} onHide={() => setPaymentModalVisible(false)}>
                    <div className="flex flex-column col-6 w-6">
                        <CalendarField name="paymentConfirmationDate" labelText="Payment Confirmation Date" value={paymentConfirmationDate} onChange={handlePaymentConfirmationDate} />
                    </div>
                </Dialog>
            </div>
            <div>
                <Dialog header="Update GRN" visible={GRNModalVisible} style={{ width: '55vw', minHeight: "45%" }} footer={GRNModalFooterContent} onHide={() => setGRNModalVisible(false)} >
                    <div className="flex flex-column col-6 w-12">
                        <div className='flex flex-column col-12 w-12'>
                            <strong className='my-2'>Enter Remarks:</strong>
                            <textarea
                                id="remarks-text-area"
                                rows="6"
                                maxLength="400"
                                placeholder="Add GRN Remarks"
                                value={GRNUpdateObj.remarks}
                                onChange={(e) => handleGRNRemarksChange(e)}
                            />
                        </div>
                        <div className="flex col-12 w-6">
                            <div className="flex flex-column col-12 w-12">
                                <DropDownField name="grnUpdate" labelText="Update GRN" options={["Yes", "No"]} value={GRNUpdateObj.GRNValue} onChange={(e) => handleGRNOptions(e)} />
                            </div>
                            <div className='flex flex-column col-12 w-12'>
                                <CalendarField name="grnDate" labelText="GRN Date" value={GRNUpdateObj.GRNDate} onChange={handlePaymentConfirmationDate} disabled={GRNUpdateObj.GRNValue === "No"} />
                            </div>
                        </div>
                    </div>
                    <br/>
                    <br/>
                </Dialog>
            </div>
            <Loading visible={isLoading} />
        </div>

    )
}

export default AdminAccountsBuyer